import React, { useMemo, useState } from "react";
import { App, Button, Space } from "antd";
import { CopyOutlined, UploadOutlined } from "@ant-design/icons";
import MasterImageService from "services/master/image";

const imageService = new MasterImageService();

function resolveImageURL(image) {
  if (!image) return "";
  if (typeof image === "string") return String(image).trim();

  return String(
    image.url || image.lg_url || image.md_url || image.sm_url || image.src || ""
  ).trim();
}

function isLikelyImageURL(value = "") {
  const text = String(value || "").trim();
  if (!text) return false;
  if (/^data:image\//i.test(text)) return true;
  return /^https?:\/\/.+\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i.test(text);
}

async function uploadBlobAsImage(blob) {
  const formData = new FormData();
  const ext = String(blob?.type || "image/png").split("/")[1] || "png";
  const fileName = `clipboard-${Date.now()}.${ext.replace(/[^a-z0-9]/gi, "") || "png"}`;
  formData.append("file", blob, fileName);
  return imageService.upload(formData);
}

const ImageClipboardActions = ({
  image,
  onSetImage,
  onAppendImage,
  copyLabel = "Copy ảnh",
  pasteLabel = "Dán từ clipboard",
}) => {
  const { message } = App.useApp();
  const [copying, setCopying] = useState(false);
  const [pasting, setPasting] = useState(false);

  const imageURL = useMemo(() => resolveImageURL(image), [image]);

  const handleCopyImage = async () => {
    if (!imageURL) {
      message.warning("Chưa có ảnh để copy");
      return;
    }

    setCopying(true);
    try {
      if (navigator?.clipboard?.write && typeof window?.ClipboardItem !== "undefined") {
        const response = await fetch(imageURL);
        if (!response.ok) {
          throw new Error("Không thể tải ảnh để copy");
        }
        const blob = await response.blob();
        const mimeType = blob?.type || "image/png";
        await navigator.clipboard.write([new window.ClipboardItem({ [mimeType]: blob })]);
        message.success("Đã copy ảnh");
        return;
      }

      await navigator.clipboard.writeText(imageURL);
      message.success("Trình duyệt không hỗ trợ copy ảnh trực tiếp, đã copy link ảnh");
    } catch (e) {
      try {
        await navigator.clipboard.writeText(imageURL);
        message.success("Không copy được ảnh trực tiếp, đã copy link ảnh");
      } catch {
        message.error(e?.message || "Copy ảnh thất bại");
      }
    } finally {
      setCopying(false);
    }
  };

  const applyImage = (nextImage) => {
    if (onAppendImage) {
      onAppendImage(nextImage);
      return;
    }
    onSetImage?.(nextImage);
  };

  const handlePasteImage = async () => {
    setPasting(true);
    try {
      if (navigator?.clipboard?.read) {
        const clipboardItems = await navigator.clipboard.read();
        for (const item of clipboardItems) {
          const type = (item.types || []).find((x) => /^image\//i.test(x));
          if (!type) continue;

          const blob = await item.getType(type);
          const saved = await uploadBlobAsImage(blob);
          applyImage(saved);
          message.success("Đã dán ảnh từ clipboard");
          return;
        }
      }

      const text = await navigator.clipboard.readText();
      if (!text) {
        message.warning("Clipboard chưa có dữ liệu ảnh");
        return;
      }

      if (!isLikelyImageURL(text)) {
        message.warning("Clipboard không chứa ảnh hoặc link ảnh hợp lệ");
        return;
      }

      applyImage({
        url: String(text).trim(),
        sm_url: String(text).trim(),
        md_url: String(text).trim(),
        lg_url: String(text).trim(),
      });
      message.success("Đã dán link ảnh");
    } catch (e) {
      message.error(e?.message || "Dán ảnh thất bại");
    } finally {
      setPasting(false);
    }
  };

  return (
    <Space wrap>
      <Button icon={<UploadOutlined />} loading={pasting} onClick={handlePasteImage}>
        {pasteLabel}
      </Button>
      <Button icon={<CopyOutlined />} loading={copying} onClick={handleCopyImage}>
        {copyLabel}
      </Button>
    </Space>
  );
};

export default ImageClipboardActions;